import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router';
import { Button, Col, Layout, Popover, Row } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { EmptyState } from '../empty_state/EmptyState';
import HyperLink, { ENTITY_TYPE } from '../common/HyperLink';
import { UserName } from '../name_component/user/UserNameComponent';
import DateTimeHover from '../date_time_component/DateTime';
import PartAuditReferences from './PartAuditReferences';
import { getAuditFieldNameByKey, PPSL_AUDIT_TYPES } from '../../utils/AuditUtils';
import { nullSafeGet, nullSafeGetOrElse, renderCurrency } from '../../utils/DataAccessUtils';
import { FILTER_FIELD_TYPE, FILTER_VALUE_TYPE } from '../../utils/DataConstants';
import {
	downloadPpslAuditsCSVForSupplier,
	ppslAuditsCrudThunksForSupplier,
} from '../../thunks/ppsl_audits_thunks';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import { PPSL_AUDITS_CRUD_ACTION_CREATORS } from '../../actions/ppsl_audits_actions';
import { PpslAudit } from '../../types/ppsl_audits';
import { SessionStoreState } from '../../types/models';

const { Content } = Layout;

const TARGET_COLLECTION_NAME = 'partAuditsIndex';
const STOCK_LOCATIONS_FILTER_TC_NAME = 'partAuditsAdvancedFilters';

interface PartsDetailAuditsPageProps extends RouteComponentProps<{ id: string }> {
	currentUser: SessionStoreState['currentUser'];
	ppslAudits: object;
	stockLocations: object;
	updateFilters: (filters: object, targetCollectionName: string) => void;
	clearAndUpdateFilters: (filters: object, targetCollectionName: string) => void;
	fetchStockLocations: (...args: unknown[]) => Promise<unknown>;
	fetchMultipleStockLocations: (ids: number[], targetCollectionName: string) => Promise<unknown>;
	exportCsv: (params: object) => Promise<unknown>;
}

const PartsDetailAuditsPage: FC<PartsDetailAuditsPageProps> = ({
	match,
	currentUser,
	ppslAudits,
	stockLocations,
	updateFilters,
	clearAndUpdateFilters,
	fetchStockLocations,
	fetchMultipleStockLocations,
	exportCsv,
}): React.ReactElement => {
	const [exporting, setExporting] = useState(false);

	const partId = match.params.id;

	const columns = useMemo(
		() => [
			{
				title: 'Audit Type',
				dataIndex: 'auditType',
				render: (_: string) => getAuditFieldNameByKey(_),
			},
			{
				title: 'Stock Location',
				dataIndex: 'stockLocation',
				render: (_: PpslAudit['stockLocation'], record: PpslAudit) => (
					<HyperLink
						text={nullSafeGetOrElse('name', _, '--')}
						entityType={ENTITY_TYPE.PPSL}
						entityId={record.partsPerStockLocationId}
						backLinkText="Back to Part"
					/>
				),
			},
			{
				title: 'Updated By',
				dataIndex: 'createdByContact',
				render: (_: PpslAudit['createdByContact'], record: PpslAudit) =>
					_ ? <UserName contact={_} mode="inline" placement="top" /> : record.createdBy || '--',
			},
			{
				title: 'Updated At',
				dataIndex: 'createdAt',
				render: (_: string, record: PpslAudit) =>
					_ ? (
						<DateTimeHover
							timestamp={_}
							timeZone={nullSafeGet('stockLocation.timeZoneId', record)}
						/>
					) : (
						'--'
					),
			},
			{
				title: 'Quantity',
				dataIndex: 'diffQuantity',
				render: (_: PpslAudit['diffQuantity']) => _ || '0',
			},
			{
				title: 'Average Weighted Cost',
				dataIndex: 'unitCost',
				render: (_: string) => renderCurrency(currentUser)(_),
			},
			{
				title: 'Extended Cost',
				dataIndex: 'extendedCost',
				render: (_: string) => renderCurrency(currentUser)(_),
			},
			{
				title: 'Stock Location Qty',
				dataIndex: 'totalQuantity',
			},
			{
				title: 'Stock Location Cost',
				dataIndex: 'totalCost',
				render: (_: string) => renderCurrency(currentUser)(_),
			},
			{
				title: 'Reference',
				render: (_, record: PpslAudit) => <PartAuditReferences record={record} />,
			},
			{
				title: 'Notes',
				dataIndex: 'userNotes',
				render: (_: PpslAudit['userNotes'], record: PpslAudit) => _ || record.notes || '--',
			},
		],
		[currentUser]
	);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Stock Location',
				fieldName: 'stockLocationIds',
				mode: 'multiple',
				stateSlice: stockLocations,
				targetCollectionName: STOCK_LOCATIONS_FILTER_TC_NAME,
				fetch: fetchStockLocations,
				fetchMultiple: fetchMultipleStockLocations,
			},
			{
				label: 'Audit Type',
				fieldName: 'auditTypes',
				mode: 'multiple',
				type: FILTER_FIELD_TYPE.OW_MULTI_SELECT,
				items: PPSL_AUDIT_TYPES.map((value) => ({
					label: getAuditFieldNameByKey(value),
					value,
				})),
				valueType: FILTER_VALUE_TYPE.STRING,
			},
		],
		[fetchMultipleStockLocations, fetchStockLocations, stockLocations]
	);

	const onExport = useCallback(() => {
		setExporting(true);
		exportCsv({
			...nullSafeGetOrElse(`${TARGET_COLLECTION_NAME}.filters`, ppslAudits, {}),
			partId,
		}).finally(() => setExporting(false));
	}, [exportCsv, partId, ppslAudits]);

	return (
		<Content style={{ padding: '0 0.5em' }}>
			<Row style={{ margin: '16px 4px' }} gutter={12}>
				<Col span={24}>
					<PaginatedReduxTableWithHeader
						targetCollectionName={TARGET_COLLECTION_NAME}
						stateSlice={ppslAudits}
						updateFilters={updateFilters}
						clearAndUpdateFilters={clearAndUpdateFilters}
						filterConfig={filterConfig}
						rightActions={
							<Popover content="Export CSV" trigger="hover">
								<Button
									size="large"
									icon={<DownloadOutlined translate="" />}
									onClick={onExport}
									loading={exporting}
								>
									Export
								</Button>
							</Popover>
						}
						entityCollectionName="ppsl_audits"
						tableColumns={columns}
						fetchData={ppslAuditsCrudThunksForSupplier.read}
						preAppliedFilters={{ partId }}
						showHeader={true}
						hasDefaultHeaderPage={false}
						initialPagination={{ current: 1, pageSize: 10 }}
						initialSorters={{}}
						hideSearch
						keyAccessor={(el: PpslAudit) => el.id}
						emptyState={
							<EmptyState
								graphic={
									<img
										style={{ marginBottom: 8 }}
										src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
										alt="No audits found!"
									/>
								}
								headline={'No audit records found.'}
								body={null}
							/>
						}
						ignoreLocationQueryParams
					/>
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	ppslAudits: state.ppsl_audits,
	stockLocations: state.stock_locations,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollectionName) =>
		dispatch(PPSL_AUDITS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(PPSL_AUDITS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.readLite(
				{ ...params, no_pagination: true, noIsActiveFilter: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	exportCsv: (params) => dispatch(downloadPpslAuditsCSVForSupplier(params)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(PartsDetailAuditsPage)
);
